import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Field, LinkField, RichText, Text, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';

import { FetchState } from 'src/enums/FetchState';
import { SortOptions } from 'src/enums/SortOptions';

import { ComponentProps } from 'lib/ComponentProps';

import { mapFilterGroup } from 'src/mappers/models/filterGroupMapper';
import { mapResourceListItem } from 'src/mappers/models/resouceListItemMapper';

import { favouritesRequest } from 'src/services/favourites/favouritesRequest';
import { SearchRequestData } from 'src/services/search/SearchDtos';

import { ExtendedItem } from 'src/types/ExtendedItem';
import { ResourceListItem } from 'src/types/ResourceListItem';
import { SocialSharingOptions } from 'src/types/SocialSharingOptions';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { Listing } from '../_Listing/_Listing';
import { SearchListingLoading } from '../_SearchListingLoading/SearchListingLoading';
import { SortByFilter } from '../_SortBy/_SortByFilter';

type FavoritesListingProps = ComponentProps & {
	fields: {
		Title: Field<string>;
		NoFavouritesText: Field<string>;
		ShowMoreText: Field<string>;
		FavouriteButtonText: Field<string>;
		UnfavouriteText: Field<string>;
		WildCardConfig: ExtendedItem & {
			fields: {
				SearchListingWildcardPage: LinkField;
			};
		};
		FilterGroups: ExtendedItem[];
		RegisteredUserText: Field<string>;
		RCNMemberText: Field<string>;
		RCNiPlusSubscriberText: Field<string>;
		OrganisationalProductText: Field<string>;
		OpenAccessText: Field<string>;
		CPDHoursText: Field<string>;
		SubjectLinkPrependedText: Field<string>;
		ShareButtonToolTip: Field<string>;
		ShareOptions: SocialSharingOptions;
		NoShareOptions: Field<string>;
		SortByLabel: Field<string>;
		SortByOptions: ExtendedItem &
			[
				{
					fields: {
						'Id Value': Field<number>;
						Title: Field<string>;
					};
				}
			];
	};
};

export default withDatasourceCheck()<FavoritesListingProps>(FavoritesListing);

function FavoritesListing(props: FavoritesListingProps): JSX.Element {
	const formMethods = useForm<SearchRequestData>({
		defaultValues: {
			SortOption: props.fields.SortByOptions[0]?.fields['Id Value']?.value || SortOptions.FAVOURITED_DESC,
		},
	});

	const sortOptionValue = formMethods.watch('SortOption');

	const isEditing = IsSitecoreEditing();

	const [favourites, setFavourites] = useState<Array<ResourceListItem>>([]);
	const [favouritesState, setFavouritesState] = useState(FetchState.START);

	const RcnUser = useContext(RcnUserContext);
	const filterGroups = mapFilterGroup(props.fields.FilterGroups);

	useEffect(() => {
		if (RcnUser.authenticatedUserProfile) {
			(async () => {
				await favouritesRequest(true, sortOptionValue).then((response) => {
					setFavouritesState(FetchState.LOADING);

					const list = Array<ResourceListItem>();
					if (response?.data.length) {
						response.data.map((result) => {
							const item = mapResourceListItem(result, props.fields.WildCardConfig?.fields?.SearchListingWildcardPage?.value?.href);
							if (item) {
								list.push(item);
							}
						});
					}
					setFavouritesState(response.state);
					setFavourites(list);
				});
			})();
		}
	}, [
		RcnUser.authenticatedUserProfile,
		RcnUser.userFavourites,
		props.fields.WildCardConfig?.fields.SearchListingWildcardPage,
		sortOptionValue,
	]);

	const inputDataTest = () => {
		return props.fields.SortByOptions.map((x) => {
			const value = (x.fields['Id Value'] as Field<number>)?.value;
			const title = x.fields.Title?.value;

			return {
				value: value,
				label: title,
			};
		});
	};

	return (
		<section className="maxWidth">
			<h3 className="padX">
				{favourites.length} <Text field={props.fields.Title} />
			</h3>

			<FormProvider {...formMethods}>
				{favourites.length > 1 && (
					<SortByFilter label={props.fields.SortByLabel} inputData={inputDataTest()} defaultSortOption={SortOptions.FAVOURITED_DESC} />
				)}
				{favouritesState === FetchState.LOADING && <SearchListingLoading />}
			</FormProvider>

			{favouritesState === FetchState.WARNING || isEditing ? (
				<RichText field={props.fields.NoFavouritesText} className="padX" />
			) : (
				favouritesState === FetchState.SUCCESS && (
					<Listing
						items={favourites}
						filters={filterGroups}
						wildCardUrl={props.fields.WildCardConfig?.fields?.SearchListingWildcardPage?.value?.href}
						favouriteButtonText={props.fields.FavouriteButtonText}
						unfavouriteText={props.fields.UnfavouriteText}
						registeredUserText={props.fields.RegisteredUserText}
						rcnMemberText={props.fields.RCNMemberText}
						rcniPlusSubscriberText={props.fields.RCNiPlusSubscriberText}
						organisationalProductText={props.fields.OrganisationalProductText}
						openAccessText={props.fields.OpenAccessText}
						subjectLinkPrependedText={props.fields.SubjectLinkPrependedText}
						shareButtonToolTip={props.fields.ShareButtonToolTip}
						shareOptions={props.fields.ShareOptions}
						noShareOptions={props.fields.NoShareOptions}
						cpdHoursText={props.fields.CPDHoursText}
					/>
				)
			)}
		</section>
	);
}
